import React, { useState, useRef } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { X, Upload, Image, Link as LinkIcon, Check, Loader2 } from 'lucide-react';
import { uploadFile, FileInfo } from '../../services/fileUploadApi';

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageSelected: (imageUrl: string) => void;
  currentUrl?: string;
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  isOpen,
  onClose,
  onImageSelected,
  currentUrl = ''
}) => {
  const [selectedTab, setSelectedTab] = useState<'upload' | 'url'>('upload');
  const [imageUrl, setImageUrl] = useState(currentUrl);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentUrl || null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<FileInfo | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 25MB)
    const maxSize = 25 * 1024 * 1024; // 25MB in bytes
    if (file.size > maxSize) {
      alert(`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of 25MB. Please choose a smaller image.`);
      return;
    }
    
    // Show upload state
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Upload file to server
      const response = await uploadFile(file);
      setUploadedFile(response.file);
      setPreviewUrl(response.file.url);
              // File uploaded successfully
    } catch (error) {
      console.error("File upload error:", error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle drag events
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    
    // Check file size (max 25MB)
    const maxSize = 25 * 1024 * 1024; // 25MB in bytes
    if (file.size > maxSize) {
      alert(`File size (${(file.size / (1024 * 1024)).toFixed(2)}MB) exceeds the maximum allowed size of 25MB. Please choose a smaller image.`);
      return;
    }
    
    // Show upload state
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // Upload file to server
      const response = await uploadFile(file);
      setUploadedFile(response.file);
      setPreviewUrl(response.file.url);
              // Dropped file uploaded successfully
    } catch (error) {
      console.error("File upload error:", error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };
  
  // Handle URL input change
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageUrl(e.target.value);
    
    // Update preview only if the URL looks valid
    if (e.target.value.match(/^https?:\/\/.+\.(jpeg|jpg|png|gif|svg)(\?.*)?$/i)) {
      setPreviewUrl(e.target.value);
    } else if (previewUrl && previewUrl !== currentUrl) {
      setPreviewUrl(null);
    }
  };
  
  // Handle confirmation
  const handleConfirm = () => {
    if (selectedTab === 'upload' && uploadedFile) {
      // For uploaded files, we need to construct the full URL
      const fullUrl = `${window.location.origin}/api/files/app/uploads/${uploadedFile.stored_name}`;
      onImageSelected(fullUrl);
    } else if (selectedTab === 'url' && imageUrl) {
      onImageSelected(imageUrl);
    }
    onClose();
  };

  // Check if the form is valid for submission
  const isFormValid = () => {
    if (selectedTab === 'upload') {
      return !!uploadedFile;
    } else if (selectedTab === 'url') {
      return !!imageUrl && imageUrl.trim() !== '';
    }
    return false;
  };
  
  // Handle tab change
  const handleTabChange = (tab: 'upload' | 'url') => {
    setSelectedTab(tab);
    if (tab === 'url') {
      setPreviewUrl(imageUrl && imageUrl.match(/^https?:\/\/.+\.(jpeg|jpg|png|gif|svg)(\?.*)?$/i) ? imageUrl : null);
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Tenant Logo"
      size="md"
    >
      <div className="mb-4">
        <div className="flex border-b border-gray-700">
          <button
            className={`px-4 py-2 font-medium text-sm ${selectedTab === 'upload' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
            onClick={() => handleTabChange('upload')}
          >
            Upload Image
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm ${selectedTab === 'url' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
            onClick={() => handleTabChange('url')}
          >
            Image URL
          </button>
        </div>
      </div>
      
      {selectedTab === 'upload' && (
        <div className="mb-4">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-gray-700 hover:border-gray-600'}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/jpeg,image/png,image/gif,image/svg+xml"
              onChange={handleFileChange}
            />
            
            {isUploading ? (
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Uploading image...</p>
                <p className="text-xs text-gray-500">Please wait while we upload your image</p>
                <p className="text-xs text-blue-500 mt-1">This should be much faster now!</p>
              </div>
            ) : uploadError ? (
              <div className="text-center">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <X size={24} className="text-red-500" />
                </div>
                <p className="text-sm text-red-400 mb-2">Upload failed</p>
                <p className="text-xs text-gray-400 mb-3">{uploadError}</p>
                <button
                  type="button"
                  onClick={() => setUploadError(null)}
                  className="text-xs text-blue-400 hover:text-blue-300 underline"
                >
                  Try again
                </button>
              </div>
            ) : previewUrl ? (
              <div className="relative">
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  className="max-h-48 mx-auto object-contain rounded"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewUrl(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="absolute top-2 right-2 bg-gray-800 p-1 rounded-full hover:bg-gray-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <>
                <Upload size={36} className="mx-auto mb-3 text-gray-500" />
                <p className="mb-1 font-medium">Drag and drop or click to upload</p>
                <p className="text-sm text-gray-400">Supports JPG, PNG, GIF, SVG (Max 25MB)</p>
                <p className="text-xs text-blue-500 mt-1">Files are stored on server for faster access</p>
              </>
            )}
          </div>
        </div>
      )}
      
      {selectedTab === 'url' && (
        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Image URL</label>
          <div className="flex items-center gap-2">
            <div className="flex-grow relative">
              <LinkIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={imageUrl}
                onChange={handleUrlChange}
                placeholder="https://example.com/logo.png"
                className="form-input pl-10 w-full bg-gray-800/70 border border-gray-700"
              />
            </div>
          </div>
          
          {previewUrl && (
            <div className="mt-4 relative">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="max-h-48 mx-auto object-contain rounded"
                onError={() => setPreviewUrl(null)}
              />
            </div>
          )}
        </div>
      )}
      
      <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-700">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirm}
          disabled={!isFormValid()}
        >
          <Check size={16} className="mr-1.5" />
          Select Image
        </Button>
      </div>
    </Modal>
  );
};

export default ImageUploadModal; 